/**
 * Tier-1 — the currency a tenant's users see money in.
 *
 * DISPLAY ONLY. Every amount in this system is stored as USD microdollars, and
 * `rate` is a multiplier applied by `formatMoney` at render time. It is not an
 * FX feed and never charges anyone — which is exactly why every converted figure
 * is marked `≈` (`APPROXIMATE_CURRENCY_NOTE`). An operator who cannot see that
 * distinction will read a conversion as an exact bill, so the preview below
 * shows the marker rather than a bare number.
 *
 * ── The two modes (0154) ──────────────────────────────────────────────
 * The rate has two states, and telling them apart is the point of this screen:
 *
 *  - **Derived** (the default, stored as `NULL`) — the rate follows
 *    `billing_settings.credit_rate_minor_per_usd`, i.e. what a customer
 *    actually pays per US$1.00 of credit. It can never drift from the receipt,
 *    and re-pricing credit moves it automatically. There is nothing to keep
 *    current, which is why this design has NO staleness warning: if you ever
 *    want one, the rate has stopped being derived and that is the bug.
 *  - **Pinned** — an explicit number, for an enterprise contract struck at a
 *    rate of its own. It is a real override: it stops tracking the credit rate
 *    silently, so the mode has to be a visible choice rather than a side effect
 *    of typing in the field.
 *
 * The derived option is hidden when `derivedRate` is null — the deployment
 * charges in a currency this tenant does not display, so the credit rate cannot
 * produce an honest number and the server would refuse to use it anyway.
 *
 * This is the first UI for `tenant_settings.display_currency`. The column, its
 * repo setter and its audit action have existed since budget-governance Phase 8
 * with no writer at all — the only ways to set it were raw SQL, which CLAUDE.md
 * forbids on production, and the demo seed script.
 *
 * It sits beside the model allow-list in the expanded row because both are
 * per-tenant platform decisions about how that tenant experiences spend, and
 * the table itself is already at its width budget.
 */
import { useMemo, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { DirtyBar, useDraftState } from '@papan/core-ui/ui'
import {
  SUPPORTED_DISPLAY_CURRENCIES, formatMoneyLabeled, APPROXIMATE_CURRENCY_NOTE,
  currencyMinorUnitExponent, type DisplayRateSource,
} from '@papan/types'
import { budgetApi } from '../../client/budget'

/** Mirrors the route's bounds. Below the minimum the rate quantizes to zero in
 *  `numeric(18,6)` and every figure renders as $0.00; the maximum is far above
 *  any real currency. Checked here as well as server-side so the editor never
 *  offers a state the server would refuse. */
const MIN_RATE = 0.000001
const MAX_RATE = 1_000_000

/** What to convert in the preview when the tenant has spent nothing this
 *  period. A preview of $0.00 demonstrates nothing about the rate. */
const SAMPLE_USD_MICRO = 100_000_000 // $100

type Mode = 'derived' | 'pinned'

export function DisplayCurrencyEditor({
  tenantId,
  tenantName,
  currency,
  /** The RESOLVED rate — whatever the chain produced, in either mode. */
  rate,
  /** Which step produced it; `explicit` is the only one that means "pinned". */
  rateSource,
  /** What clearing the override would yield, or `null` when the credit rate
   *  cannot produce an honest rate for this currency. */
  derivedRate,
  /** The tenant's spend this period, so the preview converts real money. */
  periodCostBillingMicro,
  disabled,
  onSaved,
}: {
  tenantId: string
  tenantName: string
  currency: string
  rate: number
  rateSource: DisplayRateSource
  derivedRate: number | null
  periodCostBillingMicro: number
  disabled?: boolean
  onSaved: () => void
}) {
  const { t } = useTranslation('core-ui')
  // ONE draft for the three linked fields (picking USD also sets the rate), over
  // the row's stored values (#897, kept across a phone/desktop layout swap).
  // Until something is changed it IS the stored row, so a reload shows through
  // — which is what the re-seed effect this replaced did; once changed, a
  // reload no longer overwrites the edit. Save and Discard drop it.
  // The rate is held as text: parsing on every keystroke turns "4." into 4 and
  // fights the operator mid-type.
  const draftKey = `customer:${tenantId}:currency`
  const [draft, setDraft, { clear: clearDraft, settle: settleDraft }] = useDraftState<{ currency: string; mode: Mode; rate: string }>(
    draftKey,
    () => ({ currency, mode: rateSource === 'explicit' ? 'pinned' : 'derived', rate: String(rate) }),
  )
  const { currency: draftCurrency, mode: draftMode, rate: draftRate } = draft
  const setDraftCurrency = (next: string) => setDraft(d => ({ ...d, currency: next }))
  const setDraftMode = (next: Mode) => setDraft(d => ({ ...d, mode: next }))
  const setDraftRate = (next: string) => setDraft(d => ({ ...d, rate: next }))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** `''` = effective now. Local time from `datetime-local`, no zone. */
  const [effectiveFrom, setEffectiveFrom, { clear: clearEffectiveFrom }] = useDraftState(`${draftKey}:effectiveFrom`, '')

  const parsed = Number(draftRate)
  const isUsd = draftCurrency === 'USD'
  // USD is canonical: it carries no multiplier at all, so neither mode is a
  // choice there and the whole control collapses to a single fixed state.
  // Deriving 5.50 onto USD would render US$100 as "$550".
  const derivable = !isUsd && derivedRate !== null
  const mode: Mode = isUsd ? 'derived' : (derivable ? draftMode : 'pinned')

  const rateValid = draftRate.trim() !== '' && Number.isFinite(parsed)
    && parsed >= MIN_RATE && parsed <= MAX_RATE
  // USD at anything but 1 isn't a second spelling of anything — it renders every
  // dollar figure at the wrong amount. Refused here and at the route.
  const usdRateInvalid = isUsd && rateValid && parsed !== 1
  // In derived mode the field is inert and its contents are irrelevant to the
  // save, so its validity must not gate the button.
  const valid = mode === 'derived' || (rateValid && !usdRateInvalid)

  /** What the save would actually send: `null` clears the override. */
  const payloadRate = mode === 'derived' ? null : parsed
  /** What the tenant would then SEE — the resolved value, which for USD and for
   *  derived mode is not what sits in the input. */
  const effectiveRate = isUsd ? 1 : (mode === 'derived' ? (derivedRate ?? 1) : parsed)

  // The mode IN FORCE, the way `mode` reads the draft: a rate that can't be
  // derived (the deployment default for a currency other than billing's) is a
  // pinned figure whatever its source. Measured against the stored source
  // alone, that tenant opened "changed" — a Discard that changed nothing — on a
  // form nobody had touched. From the STORED values, never the draft: `reset()`
  // restores this, and a draft currency (USD picked, then Discard) must not
  // decide what it restores.
  const storedUsd = currency === 'USD'
  const storedDerivable = !storedUsd && derivedRate !== null
  const savedMode: Mode = storedUsd ? 'derived' : (storedDerivable ? (rateSource === 'explicit' ? 'pinned' : 'derived') : 'pinned')
  const dirty = draftCurrency !== currency
    || mode !== savedMode
    || (mode === 'pinned' && parsed !== rate)

  const preview = useMemo(() => {
    if (!valid) return null
    const micro = periodCostBillingMicro > 0 ? periodCostBillingMicro : SAMPLE_USD_MICRO
    return {
      sample: periodCostBillingMicro <= 0,
      text: formatMoneyLabeled(micro, { currency: draftCurrency, rate: effectiveRate }),
    }
  }, [valid, periodCostBillingMicro, draftCurrency, effectiveRate])

  /** Picking USD snaps the rate to 1 — the only value it may hold — so the
   *  operator isn't left staring at a blocked Save wondering which field is
   *  wrong. Leaving USD restores whatever they had typed before. */
  const pickCurrency = (next: string) => {
    setDraftCurrency(next)
    if (next === 'USD') setDraftRate('1')
  }

  /** Switching to pinned seeds the field with the rate currently in effect, so
   *  the operator adjusts a real number instead of starting from whatever text
   *  was last left in the box. */
  const pickMode = (next: Mode) => {
    setDraftMode(next)
    if (next === 'pinned' && derivedRate !== null) setDraftRate(String(derivedRate))
  }

  // Back to the stored row. (The fallback's mode is `savedMode` wherever the
  // mode is read: `mode` ignores the draft mode for USD and non-derivable rates.)
  const reset = () => {
    clearDraft()
    setError(null)
  }

  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      const res = await budgetApi.setDisplayCurrency(
        tenantId, draftCurrency, payloadRate,
        // `datetime-local` is local time with no zone; `new Date()` reads it as
        // local and `toISOString()` converts. Blank means now.
        effectiveFrom ? new Date(effectiveFrom).toISOString() : undefined,
      )
      // The SERVER says whether it applied now — re-deriving it from this
      // clock disagrees with the server's under latency or skew, and a draft
      // settled against a value that never moves sticks.
      const scheduled = res?.applied === false
      clearEffectiveFrom()
      // A SCHEDULED change leaves today's stored values as they are (the setter
      // does not touch the cache columns), so nothing will move: cleared. An
      // immediate one is settled — the stored row only updates when
      // CustomerDetail's reload (`onSaved`) lands, and clearing now flashed the
      // old currency back.
      if (scheduled) clearDraft()
      else settleDraft()
      onSaved()
    } catch (e: any) {
      setError(e?.message || t('settings.budget.currency.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  const busy = !!disabled || saving
  // `datetime-local` wants local time, no seconds. The past is refused by the
  // route AND by a trigger; this is the affordance, not the enforcement.
  const minLocal = new Date(Date.now() - new Date().getTimezoneOffset() * 60_000)
    .toISOString().slice(0, 16)

  return (
    <div className="mt-2 pt-2 border-t themed-border">
      <div className="flex flex-wrap items-center gap-2 mb-1.5">
        <span className="text-[11px] themed-text-secondary font-medium">
          {t('settings.budget.currency.title')}
        </span>
        <select
          value={draftCurrency}
          disabled={busy}
          onChange={(e) => pickCurrency(e.target.value)}
          aria-label={`${tenantName} — ${t('settings.budget.currency.title')}`}
          className="themed-input border rounded px-1.5 py-0.5 text-[11px] outline-none focus:border-blue-500 disabled:opacity-50"
        >
          {SUPPORTED_DISPLAY_CURRENCIES.map((code) => (
            <option key={code} value={code}>{code}</option>
          ))}
        </select>

        {derivable && (
          <select
            value={mode}
            disabled={busy}
            onChange={(e) => pickMode(e.target.value as Mode)}
            aria-label={`${tenantName} — ${t('settings.budget.currency.rateMode')}`}
            className="themed-input border rounded px-1.5 py-0.5 text-[11px] outline-none focus:border-blue-500 disabled:opacity-50"
          >
            <option value="derived">{t('settings.budget.currency.modeDerived')}</option>
            <option value="pinned">{t('settings.budget.currency.modePinned')}</option>
          </select>
        )}

        <label className="flex items-center gap-1 text-[11px] themed-text-muted">
          {t('settings.budget.currency.rate')}
          <input
            type="text"
            inputMode="decimal"
            // In derived mode the field mirrors the credit rate read-only, so
            // the operator can see the number without it looking editable.
            value={mode === 'derived' ? String(effectiveRate) : draftRate}
            // USD may only ever be 1, and a derived rate is not typed — the
            // field is inert rather than a trap the operator discovers by
            // having Save refuse them.
            disabled={busy || isUsd || mode === 'derived'}
            onChange={(e) => setDraftRate(e.target.value)}
            aria-label={`${tenantName} — ${t('settings.budget.currency.rate')}`}
            className="themed-input border rounded px-1.5 py-0.5 text-[11px] w-20 tabular-nums outline-none focus:border-blue-500 disabled:opacity-50"
          />
        </label>
      </div>

      {/* The whole point of the screen: what a user of this tenant will
          actually read, marker included. */}
      {preview && (
        <p className="text-[11px] themed-text-muted mb-1.5" title={APPROXIMATE_CURRENCY_NOTE}>
          {preview.sample
            ? t('settings.budget.currency.previewSample', { value: preview.text })
            : t('settings.budget.currency.preview', { value: preview.text })}
        </p>
      )}

      {mode === 'pinned' && !rateValid && (
        <p className="text-[11px] text-amber-300 mb-1.5">
          {t('settings.budget.currency.rateRange', { min: MIN_RATE, max: MAX_RATE })}
        </p>
      )}
      {usdRateInvalid && (
        <p className="text-[11px] text-amber-300 mb-1.5">{t('settings.budget.currency.usdIsCanonical')}</p>
      )}

      {/* Why the mode matters, stated where the choice is made: derived tracks
          the credit rate forever, pinned silently stops. */}
      {!isUsd && mode === 'derived' && derivedRate !== null && (
        <p className="text-[11px] themed-text-muted mb-1.5">
          {t('settings.budget.currency.derivedFromCreditRate', {
            // The currency's OWN exponent, not a hardcoded 2 — "5.50" for MYR,
            // "150" for JPY. Same rule as `formatCurrencyMinor`.
            rate: derivedRate.toFixed(currencyMinorUnitExponent(draftCurrency)),
            currency: draftCurrency,
          })}
        </p>
      )}
      {!isUsd && mode === 'pinned' && derivable && (
        <p className="text-[11px] themed-text-muted mb-1.5">
          {t('settings.budget.currency.pinnedStopsTracking')}
        </p>
      )}

      <p className="text-[11px] themed-text-muted mb-1.5">{t('settings.budget.currency.displayOnly')}</p>

      {/* ── When it takes effect (0173) ────────────────────────────────────
          A rate change is APPENDED as an epoch, never edited in place, so this
          customer's past months keep the rate that was in force when the spend
          happened. Saying so here is the point: without it an operator hesitates
          to change a pin at all, because the old behaviour DID restate history. */}
      {dirty && (
        <label className="flex flex-col gap-1 mb-1.5">
          <span className="text-[11px] themed-text-secondary">
            {t('settings.budget.currency.effectiveFrom', { defaultValue: 'Takes effect' })}
          </span>
          <input
            type="datetime-local"
            className="themed-input border rounded px-1.5 py-0.5 text-[11px] w-52 outline-none focus:border-blue-500 disabled:opacity-50"
            value={effectiveFrom}
            min={minLocal}
            disabled={busy}
            aria-label={`${tenantName} — ${t('settings.budget.currency.effectiveFrom', { defaultValue: 'Takes effect' })}`}
            onChange={(e) => setEffectiveFrom(e.target.value)}
          />
          <span className="text-[10px] themed-text-muted">
            {t('settings.budget.currency.effectiveHint', {
              defaultValue: 'Blank means immediately. Spend already recorded keeps the rate it was '
                + 'incurred at — changing this never restates a past month.',
            })}
          </span>
        </label>
      )}

      {/* F-33 — the shared bar. An invalid draft goes through `saveDisabled`,
          NOT `busy`: it must refuse Save while leaving Discard live, which is
          the way out of exactly that state. */}
      <DirtyBar
        count={dirty ? 1 : 0}
        busy={busy}
        saveDisabled={!valid}
        onSave={save}
        onDiscard={reset}
        pendingLabel={t('settings.budget.currency.unsaved', {
          defaultValue: 'Display currency has unsaved changes.',
        })}
        discardLabel={t('settings.budget.pricing.discard')}
        saveLabel={t('common:save')}
        savingLabel={t('common:saving')}
      />

      {error && <p className="text-[11px] text-red-400 mt-1">{error}</p>}
    </div>
  )
}
